import { AxiosResponse } from 'axios';
import { AuthGet, AuthPost } from './Routing/Requests';
import { Link, Typography, createTheme, styled } from '@mui/material';
import * as React from 'react';
import { red } from '@mui/material/colors';
import { DateTime } from 'luxon';
import {
    ApiCustomer,
    ApiRoles,
    ApiUser,
    Blog,
    Customer,
    MultiSelectOption,
    PortalFile,
    ProductLeadTimes,
    Project,
    User,
} from '../interfaces/Interfaces';
import { TextareaAutosize as BaseTextareaAutosize } from '@mui/base/TextareaAutosize';

const editUser = (
    email: string,
    firstName: string,
    lastName: string,
    companyId: string,
    token: string,
) => {
    // Check if credentials are valid before using
    if (email && firstName && lastName && companyId && token) {
        AuthPost(
            '/user/edit',
            { email, firstName, lastName, companyId },
            {
                'x-access-token': token,
                canCache: true,
            },
        ).then((res: AxiosResponse<Array<ApiRoles>>) => {
            if (res?.status === 200) {
                return res;
            } else {
                throw new Error(res.toString());
            }
        });
    } else {
        throw new Error('Invalid or Missing Data');
    }
};

const editUserActive = (email: string, active: boolean, token: string) => {
    // Check if credentials are valid before using
    if (email && active !== null && active !== undefined && token) {
        return AuthPost(
            '/user/active',
            { email, active },
            {
                'x-access-token': token,
                canCache: true,
            },
        ).then((res: AxiosResponse<Array<ApiRoles>>) => {
            if (res?.status === 200) {
                return res;
            } else {
                throw new Error(res.toString());
            }
        });
    } else {
        throw new Error('Invalid or Missing Data');
    }
};

const genericFetch = (route: string, token: string) => {
    return AuthGet(route, {
        'x-access-token': token,
        canCache: true,
    }).then((res: AxiosResponse) => {
        if (res?.status === 200) {
            return res;
        } else {
            console.error(res);
            return res;
        }
    });
};

const getAllBlogs = (token: string): Promise<AxiosResponse<Array<Blog>>> =>
    genericFetch('/blog/all', token);

const getAllProductLeadTimes = (
    token: string,
): Promise<AxiosResponse<Array<ProductLeadTimes>>> =>
    genericFetch('/GetProductLeadTimes', token);

const genericCustomerFetch = (
    route: string,
    customerId: string,
    token: string,
) => {
    return AuthGet(route, {
        Customer_id: customerId,
        'x-access-token': token,
        canCache: true,
    }).then((res: AxiosResponse) => {
        if (res?.status === 200) {
            return res;
        } else {
            throw new Error(res.toString());
        }
    });
};

const fetchCustomer = (
    customerId: string,
    token: string,
): Promise<AxiosResponse<Customer>> =>
    genericCustomerFetch('/Customers/customer_id', customerId, token);

const fetchOpenProjects = (
    customerId: string,
    token: string,
): Promise<AxiosResponse<Array<Project>>> =>
    genericCustomerFetch(
        '/Customers/Customer_id/OpenProjects',
        customerId,
        token,
    );

const fetchOpenTasks = (customerId: string, token: string) =>
    genericCustomerFetch('/Customers/Customer_id/OpenTasks', customerId, token);

const fetchPortalFiles = (
    customerId: string,
    token: string,
): Promise<AxiosResponse<Array<PortalFile>>> =>
    genericCustomerFetch('/CustomerPortalFiles', customerId, token);

// TODO remove ANY
// eslint-disable-next-line
const Copyright = (props: any) => {
    return (
        <Typography
            variant="body2"
            color="text.secondary"
            align="center"
            {...props}
        >
            <Link color="inherit" href="https://aptmfg.com/">
                APT Manufacturing Solutions
            </Link>
            {' © '}
            {DateTime.now().setZone('America/New_York').toFormat('yyyy')}
        </Typography>
    );
};

const defaultTheme = createTheme({
    palette: {
        primary: {
            main: red[500],
        },
        secondary: {
            main: red[500],
        },
        warning: {
            main: '#FFEA00',
        },
    },
});

const formatDateNoTime = (date: DateTime) => {
    return date.toFormat('yyyy-MM-dd'); // yyyy-MM-dd
};

const formatDateNoTimeMonth = (date: DateTime) => {
    return date.toFormat('MM-dd-yyyy'); // MM-dd-yyyy
};

const formatTimeNoDate = (date: DateTime) => {
    return date.toFormat('HH:mm:ss'); // HH:mm:ss
};

const formatTimeNoDateIncludeTZ = (date: DateTime) => {
    return date.toFormat('h:mm a ZZZZ'); // h:mm PM EDT (12 hour format)
};

const formatTimeWithSecondsNoDateIncludeTZ = (date: DateTime) => {
    return date.toFormat('h:mm:ss a ZZZZ'); // h:mm:ss PM EDT (12 hour format)
};

const formatDateWithTime = (date: DateTime) => {
    return date.toFormat('yyyy-MM-dd HH:mm:ss'); // yyyy-MM-dd HH:mm:ss
};

const formatDateWithTimeAndT = (date: DateTime) => {
    return date.toFormat("yyyy-MM-dd'T'HH:mm:ss"); // yyyy-MM-dd'T'HH:mm:ss
};

const fetchEmployee = (username: string) => {
    return AuthGet('/GetEmployeeUsername', {
        employeeUsername: username,
        canCache: true,
    });
};

const convertMinutesToHours = (mins: number) => {
    const hours = mins / 60;
    const rhours = Math.floor(hours);
    const minutes = (hours - rhours) * 60;
    const rminutes = Math.round(minutes);
    return rhours + ' hrs ' + rminutes + ' mins';
};

const getDateThisWeekStart = () => {
    return DateTime.now()
        .setZone('America/New_York')
        .startOf('week')
        .toFormat('yyyy-MM-dd'); // yyyy-MM-dd
};

const getDateLastWeekStart = () => {
    return DateTime.now()
        .setZone('America/New_York')
        .minus({ days: 7 })
        .startOf('week')
        .toFormat('yyyy-MM-dd'); // yyyy-MM-dd
};

const getDateLastWeekEnd = () => {
    return DateTime.now()
        .setZone('America/New_York')
        .minus({ days: 7 })
        .startOf('week')
        .plus({ days: 6 })
        .toFormat('yyyy-MM-dd'); // yyyy-MM-dd
};

const userMapper = (user: ApiUser): User => {
    return {
        email: user.User_Email,
        firstName: user.First_Name,
        lastName: user.Last_Name,
        customerId: user.Customer_ID,
        active: user.Active,
        roles: user.Role,
    };
};

const rolesFormatter = (roles: string): string => {
    if (roles) {
        const formattedRoles = roles.replaceAll(',', ', ').slice(0, -2);
        return formattedRoles;
    }
    return '';
};

const isAdmin = (roles: string) => roles.indexOf('ADMIN') > -1;

export const selectOptionBackToRoleMapper = (
    rolesOptionArr: Array<MultiSelectOption>,
    originalRoles: Array<ApiRoles>,
): Array<ApiRoles> => {
    return originalRoles.filter(Boolean) as ApiRoles[];
};

const roleSelectOptionMapper = (rolesArr: Array<ApiRoles>) =>
    (rolesArr ?? [])
        .filter(
            (role): role is ApiRoles =>
                !!role && typeof role.distributor_Type !== 'undefined',
        )
        .map((role) => ({
            label: role.distributor_Type,
            value: role.distributor_Type,
            option: role.distributor_Type,
            key: role.distributor_Type,
            Role: role.distributor_Type,
        }));

const customerSelectOptionMapper = (customerArr: Array<ApiCustomer>) =>
    customerArr?.map((cx) => {
        return {
            label: cx?.name,
            id: cx?.customer,
        };
    });

const customerIdStringToSelectOption = (
    customerArr: Array<ApiCustomer>,
    customer: string,
) => {
    const customerFiltered = customerArr?.filter(
        (cx) => cx?.customer === customer,
    )[0];
    return {
        label: customerFiltered?.name,
        id: customerFiltered?.customer,
    };
};

export const rolesStringToSelectOptionFormatter = (
    roles: string,
    apiRoles: Array<ApiRoles>,
): MultiSelectOption[] => {
    if (!roles) return [];
    const formattedRoles = roles
        .split(',')
        .map((r) => r.trim())
        .filter(Boolean);
    return apiRoles
        .filter((apiRole) => formattedRoles.includes(apiRole.distributor_Type))
        .map((apiRole, idx) => ({
            label: apiRole.distributor_Type,
            value: apiRole.distributor_Type,
            option: apiRole.distributor_Type,
            key: apiRole.id ?? idx,
            Role: apiRole.distributor_Type,
        }));
};

const compareArrays = (
    array1: Array<MultiSelectOption>,
    array2: Array<MultiSelectOption>,
) => {
    const array1sorted = array1.sort((a, b) => Number(a.key) - Number(b.key));
    const array2sorted = array2.sort((a, b) => Number(a.key) - Number(b.key));

    return array1sorted.every((value, index) => {
        return value?.key === array2sorted[index]?.key;
    });
};

const checkIfMultiSelectItemAddedOrRemoved = (
    currentRoles: Array<MultiSelectOption>,
    previousRoles: Array<MultiSelectOption>,
) => {
    const array1sorted = currentRoles.sort(
        (a, b) => Number(a.key) - Number(b.key),
    );
    const array2sorted = previousRoles.sort(
        (a, b) => Number(a.key) - Number(b.key),
    );

    if (array1sorted?.length !== array2sorted?.length) {
        const addRoles = array1sorted.filter(
            (item1) => !array2sorted.some((item2) => item2.key === item1.key),
        );
        const removeRoles = array2sorted.filter(
            (item2) => !array1sorted.some((item1) => item1.key === item2.key),
        );

        return {
            addRoles: addRoles,
            removeRoles: removeRoles,
        };
    }
    return {
        addRoles: [],
        removeRoles: [],
    };
};

const Textarea = styled(BaseTextareaAutosize)(
    `
        width: 100%;
        font-size: 0.875rem;
        font-weight: 400;
        line-height: 1.5;
        padding: 16.5px 14px;
        border-radius: 4px;
        border: 1px solid rgba(0, 0, 0, 0.2);

        &:hover {
        border-color: black;
        }

        &:focus {
        border-color: #f44336;
        box-shadow: 0 0 0 1px #f44336;
        }

        // firefox
        &:focus-visible {
        outline: 0;
        }
    `,
);

export {
    defaultTheme,
    Copyright,
    formatDateNoTime,
    formatDateWithTime,
    fetchEmployee,
    formatTimeNoDate,
    getDateLastWeekStart,
    getDateLastWeekEnd,
    getDateThisWeekStart,
    convertMinutesToHours,
    formatTimeNoDateIncludeTZ,
    formatDateNoTimeMonth,
    formatDateWithTimeAndT,
    formatTimeWithSecondsNoDateIncludeTZ,
    fetchCustomer,
    fetchOpenProjects,
    fetchOpenTasks,
    fetchPortalFiles,
    userMapper,
    rolesFormatter,
    editUser,
    editUserActive,
    roleSelectOptionMapper,
    customerSelectOptionMapper,
    compareArrays,
    checkIfMultiSelectItemAddedOrRemoved,
    genericCustomerFetch,
    customerIdStringToSelectOption,
    isAdmin,
    getAllBlogs,
    Textarea,
    getAllProductLeadTimes,
};
